import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { EJobType } from '../job-type.enum';
import { IJob } from '../IJob';
import { DateUtil } from 'app/core/utils/DateUtil';

@Component({
  selector: 'job-steps',
  templateUrl: 'jobs-steps.component.html',
  styleUrls: ['jobs-steps.component.scss']
})
export class JobsStepsComponent implements OnInit, OnChanges {
  @Input() applyDisabled = false;
  @Input() currentJob;
  @Input() jobType: EJobType;
  @Input() isDevelopment: boolean;
  @Input() areOtherTabsValid: boolean = false;
  @Input() jobStates: { [key: string]: { rescheduleJob: boolean, switchActivated: boolean, originalNextRuntimeDate: Date, nextRuntimeDate: Date } } = {};
  @Output() validationChange = new EventEmitter<boolean>();
  @Output() saveJob = new EventEmitter<IJob>();
  @Output() cancel = new EventEmitter();
  public jobTypes = EJobType;
  public isValidStepName: { [stepId: string]: boolean } = {};
  public validationMessage: string = '';
  public showValidationMessage: boolean = false;

  ngOnInit(): void {
    if (this.currentJob?.steps) {
        this.initStepNameValidation();
    } else {
        this.validationChange.emit(true);
    }
}

ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentJob) {
        if (this.currentJob?.steps) {
            this.initStepNameValidation();
        } else {
            this.validationChange.emit(true);
        }
    }
    
    if (changes.areOtherTabsValid) {
        this.updateValidationMessage();
    }
}

  getOrCreateStepId(step: any): string {
    if (step.idJob) return step.idJob;
    if (!step.uuid) step.uuid = uuidv4();
    return step.uuid;
  }

  getStepName(step: any): string {
    return step.jsName || step.name;
  }

  setStepName(step: any, value: string): void {
    if (step.jsName !== undefined) {
      step.jsName = value;
    } else {
      step.name = value;
    }
    this.initStepNameValidation();
  }

  getStepDescription(step: any): string {
    return step.jsDescription || step.description;
  }

  setStepDescription(step: any, value: string): void {
    if (step.jsDescription !== undefined) {
      step.jsDescription = value;
    } else {
      step.description = value;
    }
  }

  public applyChanges() {
    // Sincronizar con el estado compartido de settings
    if (this.jobStates[this.currentJob.guid]) {
      DateUtil.setTimeToDate(this.jobStates[this.currentJob.guid].nextRuntimeDate, DateUtil.getTimeFromDate(this.jobStates[this.currentJob.guid].nextRuntimeDate));
      this.currentJob.nextRuntime = DateUtil.returnDateToSave(this.jobStates[this.currentJob.guid].nextRuntimeDate);
      
      // Actualizar schedules si existen
      if (this.currentJob.schedules) {
        this.currentJob.schedules.forEach(schedule => {
          schedule.nextRunTime = this.currentJob.nextRuntime;
        });
      }
    }
    
    this.saveJob.emit(this.currentJob);
  }
  
  public cancelChanges() {
    this.cancel.emit();
  }

  private validateStepName(stepName: string, currentStep?: any): boolean {
    const isValidFormat = /^[a-zA-Z0-9]*$/.test(stepName);
    const isValidLength = stepName.length >= 1 && stepName.length <= 26;

    if (!isValidFormat || !isValidLength) return false;

    if (this.currentJob?.steps) {
      let count = 0;
      for (const step of this.currentJob.steps) {
        if (this.getStepName(step) === stepName) {
          if (currentStep && step === currentStep) continue;
          count++;
        }
      }
      if (count > 0) return false;
    }

    return true;
  }

  private initStepNameValidation(): void {
    this.isValidStepName = {};
    if (this.currentJob?.steps) {
      for (const step of this.currentJob.steps) {
        this.isValidStepName[this.getOrCreateStepId(step)] = this.validateStepName(this.getStepName(step), step);
      }
    }
    this.validationChange.emit(this.allStepNamesValid);
    this.updateValidationMessage();
  }

  private updateValidationMessage(): void {
    if (this.isDevelopment) {
      this.resetValidationMessage();
      return;
    }

    const hasInvalidStepNames = !this.allStepNamesValid;
    const hasOtherValidationIssues = !this.areOtherTabsValid;

    this.showValidationMessage = hasInvalidStepNames || hasOtherValidationIssues;
    this.validationMessage = this.getValidationMessageKey(hasInvalidStepNames, hasOtherValidationIssues);
  }

  private resetValidationMessage(): void {
    this.showValidationMessage = false;
    this.validationMessage = '';
  }

  private getValidationMessageKey(hasInvalidStepNames: boolean, hasOtherValidationIssues: boolean): string {
    if (!hasInvalidStepNames && !hasOtherValidationIssues) {
      return '';
    }
    
    if (hasInvalidStepNames && hasOtherValidationIssues) {
      return 'JOBS.STEPS_SCHEDULES_NAME_AND_OTHER_TABS_VALIDATION_MESSAGE';
    }
    
    return hasInvalidStepNames 
      ? 'JOBS.STEPS_SCHEDULES_NAME_VALIDATION_MESSAGE'
      : 'JOBS.USERJOBS_VALIDATION_ERRORS_IN_OTHER_TABS';
  }

  get allStepNamesValid(): boolean {
    const values = Object.values(this.isValidStepName);
    if (!values.length) return true;
    return values.every(v => v === true);
  }
}
